import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { viewableFields } from '@/features/records/utils/fieldAccess'
import { useForm } from '@/hooks/useForm'
import { useMailConnections } from '@/hooks/useMail'
import { usePermission } from '@/hooks/usePermission'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { FormFieldType } from '@/types/enums'

/** The settings of a SEND_EMAIL action that go beyond to / subject / body, as stored in its metadata. */
export interface EmailDeliveryMetadata {
  replyToField?: string
  replyTarget?: string
  sendFrom?: string
  sendFromConnectionId?: string
  deliveryMode?: string
  cc?: string
  bcc?: string
  attachmentFields?: string[]
}

const NONE = '__none__'

function Setting({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-neutral-600">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-neutral-500">{hint}</p>}
    </div>
  )
}

/**
 * Reply, sender, draft, cc/bcc and attachment settings of an email action. A setting left at its default
 * is removed from the metadata, so existing actions keep their stored shape.
 */
export function EmailDeliveryFields<M extends EmailDeliveryMetadata>({ metadata, onChange, recordGroupId }: {
  metadata: M
  onChange: (metadata: M) => void
  recordGroupId: string
}) {
  const { form } = useForm(recordGroupId, { enabled: !!recordGroupId })
  const { can } = usePermission()
  const { data: connections } = useMailConnections()
  const fields = viewableFields(form?.fields ?? [])
  const emailFields = fields.filter((f) => f.type === FormFieldType.EMAIL_MESSAGE)
  const fileFields = fields.filter((f) => f.type === FormFieldType.FILE_UPLOAD)
  const sharedMailboxes = (connections ?? []).filter((c) => c.scope === 'WORKSPACE' && c.status === 'ACTIVE')
  const canUseShared = can(PERMISSION_KEYS.MAILBOX_USE_WORKSPACE)

  const sendFrom = metadata.sendFrom ?? 'SYSTEM'
  const set = (patch: Partial<EmailDeliveryMetadata>) => {
    const next = { ...metadata, ...patch } as Record<string, unknown>
    for (const key of Object.keys(patch)) {
      const value = next[key]
      if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) delete next[key]
    }
    onChange(next as M)
  }

  return (
    <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3">
      <Setting label="Send from">
        <Select
          value={sendFrom}
          onValueChange={(value) => set({
            sendFrom: value === 'SYSTEM' ? undefined : value,
            sendFromConnectionId: value === 'WORKSPACE' ? metadata.sendFromConnectionId : undefined,
            deliveryMode: value === 'SYSTEM' ? undefined : metadata.deliveryMode,
          })}
        >
          <SelectTrigger aria-label="Send from"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="SYSTEM">The system address</SelectItem>
            {(canUseShared || sendFrom === 'WORKSPACE') && <SelectItem value="WORKSPACE">A shared workspace mailbox</SelectItem>}
            <SelectItem value="ACTOR">The mailbox of whoever triggers it</SelectItem>
          </SelectContent>
        </Select>
      </Setting>

      {sendFrom === 'WORKSPACE' && (
        <Setting label="Workspace mailbox" hint={sharedMailboxes.length === 0 ? 'No shared mailbox is connected. Connect one under Mailboxes.' : undefined}>
          <Select value={metadata.sendFromConnectionId ?? ''} onValueChange={(value) => set({ sendFromConnectionId: value })}>
            <SelectTrigger aria-label="Workspace mailbox"><SelectValue placeholder="Choose a mailbox" /></SelectTrigger>
            <SelectContent>
              {sharedMailboxes.map((c) => <SelectItem key={c.id} value={c.id}>{c.emailAddress}</SelectItem>)}
            </SelectContent>
          </Select>
        </Setting>
      )}

      <Setting
        label="Reply to the email in"
        hint={emailFields.length > 0
          ? 'Pick a Linked email field to send this as a reply in that conversation. Recipient and subject can then be left empty.'
          : 'To reply to an email, add a Linked email field to this form first (Contact group in the form designer).'}
      >
        <Select
          value={metadata.replyToField ?? NONE}
          onValueChange={(value) => set({
            replyToField: value === NONE ? undefined : value,
            replyTarget: value === NONE ? undefined : metadata.replyTarget,
          })}
        >
          <SelectTrigger aria-label="Reply to the email in"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Nothing (a new email)</SelectItem>
            {emailFields.map((f) => <SelectItem key={f.id} value={f.key}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Setting>

      {metadata.replyToField && (
        <Setting label="Reply to">
          <Select value={metadata.replyTarget ?? 'LATEST'} onValueChange={(value) => set({ replyTarget: value === 'LATEST' ? undefined : value })}>
            <SelectTrigger aria-label="Reply to"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="LATEST">The latest message in the conversation</SelectItem>
              <SelectItem value="PICKED">The exact message that was linked</SelectItem>
            </SelectContent>
          </Select>
        </Setting>
      )}

      <Setting label="Delivery" hint={sendFrom === 'SYSTEM' ? 'Drafts need a mailbox: choose a shared mailbox or the triggering person’s.' : 'A draft is saved in the mailbox for someone to review and send.'}>
        <Select value={metadata.deliveryMode ?? 'SEND'} onValueChange={(value) => set({ deliveryMode: value === 'SEND' ? undefined : value })}>
          <SelectTrigger aria-label="Delivery"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="SEND">Send it</SelectItem>
            <SelectItem value="DRAFT" disabled={sendFrom === 'SYSTEM'}>Save as a draft</SelectItem>
          </SelectContent>
        </Select>
      </Setting>

      <div className="grid gap-3 sm:grid-cols-2">
        <Setting label="Cc">
          <Input placeholder="e.g. {{manager_email}}" value={metadata.cc ?? ''} onChange={(e) => set({ cc: e.target.value })} />
        </Setting>
        <Setting label="Bcc">
          <Input placeholder="e.g. archive@example.com" value={metadata.bcc ?? ''} onChange={(e) => set({ bcc: e.target.value })} />
        </Setting>
      </div>

      {fileFields.length > 0 && (
        <Setting label="Attach files from" hint="Every file in the chosen fields is attached (up to 20 MB in total, 3 MB from Outlook).">
          <div className="flex flex-wrap gap-3">
            {fileFields.map((f) => {
              const selected = metadata.attachmentFields ?? []
              return (
                <label key={f.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded accent-primary"
                    checked={selected.includes(f.key)}
                    onChange={(e) => set({ attachmentFields: e.target.checked ? [...selected, f.key] : selected.filter((k) => k !== f.key) })}
                  />
                  {f.label}
                </label>
              )
            })}
          </div>
        </Setting>
      )}
    </div>
  )
}
